<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div
      class="main-product__grid grid"
      :class="{
        ['main-product__grid--expanded']: galleryExpanded
      }"
      js-sticky-parent
    >
      <nav
        v-if="breadcrumbs.length > 0"
        class="main-product__breadcrumbs col xs4 desktop-hidden"
      >
        <a
          v-for="(breadcrumb, index) of breadcrumbs"
          :key="`breadcrumb-${index}`"
          class="main-product__breadcrumb text-desktop-small"
          :href="breadcrumb.link"
          :title="`foo - ${breadcrumb.text}`"
          @click="trackClick(breadcrumb)"
        >
          <span
            class="breadcrumb__text"
            v-text="breadcrumb.text"
          />
          <svg
            v-if="index != Object.keys(breadcrumbs).length - 1"
            class="breadcrumb__separator"
            width="5"
            height="7"
            viewBox="0 0 5 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 0.495807L4.01699 3.5128L1 6.52979"
              stroke="#090909"
            />
          </svg>
        </a>
      </nav>
      <div
        id="main-product__gallery-column"
        class="main-product__gallery-column col xs4 l5"
        js-sticky-parent
      >
        <product-gallery
          v-if="variant"
          ref="mobileGallery"
          :class="{
            [$class('open')]: galleryExpanded
          }"
          :product="product"
          :variant="variant"
          :image-index="selectedImage"
          @gallery-state-changed="updateGalleryState"
          @carousel-image-changed="updateGalleryIndex"
          @zoom-updated="(value) => imageZoomed = value"
        />

        <button
          v-if="imageZoomed"
          class="
              main-product__close-zoom
              main-product__form-footer-action
              text-mobile-small-uc-mobile
            "
          type="button"
          @click="closeZoom"
        >
          <icon-close />
        </button>
      </div>

      <div
        ref="contentContainer"
        class="main-product__content-column col xs4 l5 xl4"
        :class="{
          ['main-product__content-column--absolute']: formPositionUpdated,
          ['main-product__content-column--behind']: galleryExpanded
        }"
      >
        <product-title
          :liquid="liquid"
          :coming-soon="{
            contentAvailable: comingSoonContentAvailable,
            content: comingSoonContent,
            listId: comingSoonListId,
            isComingSoonProduct,
          }"
          :product="product"
        />

        <!-- Default product content -->
        <div
          ref="formSection"
          class="
            main-product__content
            main-product__sticky-section
            main-product__sticky-section--form
          "
          :class="{
            ['main-product__content--breadcrumbs']: breadcrumbs.length > 0
          }"
        >
          <nav
            v-if="breadcrumbs.length > 0"
            class="main-product__breadcrumbs col l6 mobile-hidden"
          >
            <a
              v-for="(breadcrumb, index) of breadcrumbs"
              :key="`breadcrumb-${index}`"
              class="main-product__breadcrumb text-desktop-small"
              :href="breadcrumb.link"
              :title="breadcrumb.text"
              @click="trackClick(breadcrumb)"
            >
              <span
                class="breadcrumb__text"
                v-text="breadcrumb.text"
              />
              <svg
                v-if="index != Object.keys(breadcrumbs).length - 1"
                class="breadcrumb__separator"
                width="5"
                height="7"
                viewBox="0 0 5 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 0.495807L4.01699 3.5128L1 6.52979"
                  stroke="#090909"
                />
              </svg>
            </a>
          </nav>

          <h1
            :id="`section-title-${liquid.sectionId}`"
            class="main-product__title text-desktop-medium text-regular"
            v-text="product.title || liquid.title"
          />

          <!-- Product swatches -->
          <product-swatches
            v-if="isMobile"
            v-show="showProductSwatches"
            block-class="main-product"
            load-swatches
            :product="product"
          />

          <div
            v-if="!isMobile"
            class="main-product__product-form"
          >
            <product-form
              :show-gift-wrapping="buildSubMenuDisplay.displayGiftWrapping"
              :liquid="liquid"
              :product="product || liquid.product"
              :size-order="sizeOrder"
              :variant="variant"
              :store-availability-content="storeAvailabilityContent"
              :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
              :coordinates="coordinates"
              :variant-availability-data="variantAvailabilityData"
              :low-availability-threshold="lowAvailabilityThreshold"
              :preorder="{
                contentAvailable: preOrderContentAvailable,
                content: buildPreOrderContent,
                isPreorderProduct
              }"
              :coming-soon="{
                contentAvailable: comingSoonContentAvailable,
                content: comingSoonContent,
                listId: productListId,
                isComingSoonProduct,
              }"
              :private-sale="{
                listId: productListId,
                isPrivateSaleProduct,
              }"
              :product-fit="{
                fitLabel: sizeGuideFitLabel,
                fitText: subMenuContent?.fit?.text_long,
              }"
              :show-product-swatches="showProductSwatches"
              :handle-swatch-update="handleSwatchUpdate"
              @update-product="handleProductUpdate"
              @update-variant="handleVariantUpdate"
              @size-guide-status-changed="handleSizeGuideStatusChange"
            />
          </div>
        </div>

        <div
          ref="descriptionSection"
          class="main-product__section main-product__section--description"
        >
          <store-availability
            v-if="!isMobile && variant"
            :product="product"
            :variant="variant"
            :store-availability-content="storeAvailabilityContent"
            :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
            :coordinates="coordinates"
            :variant-availability-data="variantAvailabilityData"
            is-trigger
          />

          <div
            v-if="buildSubMenuDisplay.displayGiftWrapping == false"
            class="main-product__shipping text-desktop-12-desktop text-mobile-12-mobile"
          >
            <span
              v-if="getRelevantMessaging"
              v-html="getRelevantMessaging?.form_message"
            />

            <template v-if="shippingDateLabel">
              <icon-chevron-right />
              <span v-text="shippingDateLabel" />
            </template>
          </div>

          <div
            class="
              main-product__product-description
              text-desktop-regular-desktop
              text-mobile-regular-mobile
            "
          >
            <span
              v-text="
                productDescriptionExpanded ?
                  strippedProductDescription : truncatedProductDescription"
            />
            <span>&nbsp;</span>
            <button
              v-if="!productDescriptionExpanded"
              class="main-product__product-description-button"
              type="button"
              @click="() => handleExpandProductDescription(true)"
              v-text="$string('product.description.read_more')"
            />

            <p
              v-if="isFinalSaleProduct"
              class="main-product__final-sale"
              v-html="$string('product.final_sale.final_sale_html')"
            />
          </div>
          <!-- Mobile Secondary menu - not displayed on desktop -->
          <div
            class="main-product__secondary-accordion"
            data-gtm-event="pdp-secondary-links"
          >
            <div
              v-if="getRelevantMessaging"
              class="main-product__secondary-menu-geo"
            >
              <p
                class="text-mobile-regular-mobile text-desktop-regular-desktop"
                v-text="getRelevantMessaging?.message"
              />
            </div>

            <accordion-block
              :accordion-items="buildSubMenu"
              :sub-menu-content="buildSubMenuContent"
              is-mobile
              :open-by-default="getDefaultAccordionItem()"
            />
          </div>
        </div>

        <!-- Secondary menu - not displayed on mobile -->
        <div
          v-show="!sizeGuideDisplay"
          ref="secondaryMenuSection"
          class="
            main-product__secondary-menu-wrapper
            main-product__sticky-section
            main-product__sticky-section--secondary-menu
          "
          data-gtm-event="pdp-secondary-links"
          @mouseleave="handleMenuMouseLeave"
        >
          <clickable-list
            ref="secondaryMenu"
            :links="buildSubMenu"
            link-type="action"
            class="main-product__secondary-menu"
            @link-clicked="handleSubMenuClick"
          />

          <div
            v-if="getRelevantMessaging"
            class="main-product__secondary-menu-geo"
          >
            <p
              class="text-desktop-regular-desktop"
              v-text="getRelevantMessaging?.message"
            />
          </div>
        </div>

        <div class="main-product__secondary-content">
          <overlay
            key="secondaryMenu"
            block-class="main-product-overlay"
            direction="left"
            namespace="secondaryMenu"
            :teleport="false"
            type="drawer"
          >
            <template
              #title
            >
              <span
                v-text="product.title"
              />
            </template>
            />
            <template #body>
              <div
                class="
                main-product__secondary-accordion
                main-product__secondary-accordion--desktop
              "
              >
                <accordion-block
                  :accordion-items="buildSubMenu"
                  :sub-menu-content="buildSubMenuContent"
                  :active-item="subMenuItem"
                  :spacing="false"
                  :is-mobile="false"
                  icon="plus"
                />
              </div>
            </template>

            <!-- Product USP list desktop -->
            <template
              v-if="!isMobile"
              #footer
            >
              <product-usp-list :product-usp-list-content="productUspListContent" />
            </template>
          </overlay>
        </div>

        <!-- Mobile product form -->
        <div
          v-if="isMobile"
          class="main-product__mobile-form"
        >
          <product-form
            v-if="!imageZoomed"
            :show-gift-wrapping="buildSubMenuDisplay.displayGiftWrapping"
            :content="buildSubMenuContent"
            :liquid="liquid"
            :preorder="{
              contentAvailable: preOrderContentAvailable,
              content: buildPreOrderContent,
              isPreorderProduct
            }"
            :coming-soon="{
              contentAvailable: comingSoonContentAvailable,
              content: comingSoonContent,
              listId: comingSoonListId,
              isComingSoonProduct,
            }"
            :show-product-swatches="showProductSwatches"
            :size-guide="{
              fitText: subMenuContent?.fit?.text,
            }"
            :product-fit="{
              fitLabel: sizeGuideFitLabel,
              fitText: subMenuContent?.fit?.text_long,
            }"
            :product="product"
            :variant="variant"
            :store-availability-content="storeAvailabilityContent"
            :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
            :coordinates="coordinates"
            :variant-availability-data="variantAvailabilityData"
            :low-availability-threshold="lowAvailabilityThreshold"
            @update-product="handleProductUpdate"
            @update-variant="handleVariantUpdate"
          />

          <div class="main-product__mobile-store-availability">
            <store-availability
              :product="product"
              :variant="variant"
              :store-availability-content="storeAvailabilityContent"
              :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
              :coordinates="coordinates"
              :variant-availability-data="variantAvailabilityData"
              is-trigger
            />
          </div>
        </div>

        <!-- Mobile shipping text -->
        <div
          class="
          main-product__shipping
          main-product__shipping__mobile
          text-desktop-12-desktop
          text-mobile-12-mobile"
        >
          <span
            v-if="getRelevantMessaging"
            v-html="getRelevantMessaging?.form_message"
          />

          <template v-if="shippingDateLabel">
            <icon-chevron-right />
            <span v-text="shippingDateLabel" />
          </template>
        </div>

        <!-- Product USP list mobile -->
        <div
          v-if="isMobile"
          class="main-product__section main-product__section--wide"
        >
          <product-usp-list :product-usp-list-content="productUspListContent" />
        </div>

        <!-- Complete the look -->
        <div
          ref="completeTheLookSection"
          class="main-product__sticky-section main-product__sticky-section--complete-the-look"
        >
          <complete-the-look
            :namespace="product.id ? String(product.id) : ''"
            @complete-the-look-loaded="calculateStickyElementsPosition"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object} preOrderContent - Content to be displayed if the product is
 * a preorder product.
 * @param {Object} comingSoonContent - Content to be displayed if the product is
 * a coming soon product.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} productUspListContent - Product USP list content
 * @param {String} truncatedProductDescription - Truncated product description
 * @param {String} shippingDateLabel - Shipping date label.
 * @param {String} sizeGuideFitLabel - Fit label for the size guide.
 * @param {Array} sizeOrder - Array of sizes.
 * @param {Object} subMenuContent - Content that displays when the sub-menu is
 * activated.
 * @param {Object} subMenuDisplay - Series of Booleans that toggle sub-menu
 * items.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import AccordionBlock from '~async/accordion-block/accordion-block'
import ClickableList from '~global/clickable-list/clickable-list'
import CompleteTheLook from '~async/complete-the-look/complete-the-look'
import Overlay from '~global/overlay/overlay'
import ProductTitle from '~async/product-title/product-title'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductSwatches from '~async/product-swatches/product-swatches'

import ProductUspList from '~async/product-usp-list/product-usp-list'
import StoreAvailability from '~async/store-availability/store-availability'

import IconClose from '~icons/misc/close.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import product from '~/graphql/queries/product.gql'
import { formatPDPProduct } from '~/helpers/format'
import { emitUpdateRequestObject } from '~/helpers/vue'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { mapActions, mapGetters, mapState } from 'vuex'

// eslint-disable-next-line no-undef
gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

export default {
  name: 'MainProduct',

  components: {
    AccordionBlock,
    ClickableList,
    CompleteTheLook,
    Overlay,
    ProductTitle,
    ProductForm,
    ProductGallery,
    ProductSwatches,
    ProductUspList,
    IconChevronRight,
    IconClose,
    StoreAvailability,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        sectionId: '',
        title: '',
        flatImageBackground: '',
      }),
    },

    product: {
      type: [Boolean, Object],
      default: false,
    },

    variantJson: {
      type: String,
      required: true,
    },

    comingSoonListId: {
      type: String,
      default: '',
    },

    comingSoonContent: {
      type: Object,
      default: () => ({
        text: 'null',
        bullets: [],
      }),
    },

    truncatedProductDescription: {
      type: String,
      default: () => '',
    },

    preOrderContent: {
      type: Object,
      default: () => ({
        text: 'null',
        bullets: [],
      }),
    },

    productUspListContent: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },

    storeAvailabilityContent: {
      type: Object,
      default: () => ({}),
    },

    shippingDateLabel: {
      type: String,
      default: () => '',
    },

    sizeGuideFitLabel: {
      type: String,
      default: () => '',
    },

    sizeOrder: {
      type: Array,
      default: () => [],
    },

    subMenuContent: {
      type: Object,
      default: () => ({}),
    },

    subMenuDisplay: {
      type: Object,
      default: () => ({
        displayGarderob: false,
        displayDetails: true,
        displayHelp: true,
        displayFit: true,
        displayGiftWrapping: false,
        displaySustainability: true,
      }),
    },

    useStoreAvailabilityFromLocations: {
      type: Boolean,
      default: false,
    },

    appDomain: {
      type: String,
      default: () => 'toteme.by-association-only.workers.dev',
    },

    productId: {
      type: Number,
      default: () => Number(''),
    },

    storeName: {
      type: String,
      default: () => '',
    },

    lowAvailabilityThreshold: {
      type: Number,
      default: 3,
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
    'update-product': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      variant: null,
      formPositionUpdated: false,
      galleryExpanded: false,
      galleryIndex: false,
      handle: cnvs.page.handle,
      imageZoomed: false,
      scrollState: 'idle',
      scrollDirection: 'ready',
      scrollPosition: 0,
      scrollTween: null,
      selectedImage: 0,
      sizeGuideDisplay: false,
      state: 'loading',
      subMenuItem: null,
      productDescriptionExpanded: false,
      variantAvailabilityData: [],
      coordinates: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getRelevantMessaging: 'multi-store/getRelevantMessaging',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      headerHeight: (state) => state.header.headerHeight,
      scrollY: (state) => state.index.scroll.y,
      settings: (state) => state.index.settings,
    }),

    /**
     * returns custom tag value from product tags
     * if a product tag starts with 'custom:'
     * it will return the value after the colon
     * @returns {String}
     */
    customTag() {
      const customTag = this.product?.tags?.find(tag => tag.startsWith('custom:'))
      const customTagValue = customTag ? customTag.split('custom:')[1] : ''
      return customTagValue ? customTagValue : ''
    },

    /**
     * Computes whether there is size guide content to display.
     * @returns {Boolean}
     */
    isSizeGuideAvailable() {
      return this.product?.variants?.length === 1
        ? Boolean(this.product?.sizeGuide?.fitTextLong)
        : Boolean(this.product?.sizeGuide?.sizeConversion)
    },

    /**
     * Computes whether there is a fit_body_measurement content to display.
     * @returns {Boolean}
     */
    isOnlyBodyMeasurementAvailable() {

      if (!this.product?.sizeGuide?.bodyMeasurement || this.product?.sizeGuide?.sizeConversion) {
        return false
      }
      return true
    },

    /**
     * Returns preorder content for the current product.
     */
    buildPreOrderContent() {
      return this.liquid.id === this.product?.id
        ? this.preOrderContent
        : this.product?.preOrder
    },

    /**
     * Returns a Boolean indicating if there is any preorder content to display.
     */
    preOrderContentAvailable() {
      return Boolean((
        this.buildPreOrderContent?.text !== null &&
        this.buildPreOrderContent?.text !== 'null' &&
        this.buildPreOrderContent?.text !== '') ||
        this.buildPreOrderContent?.bullets?.length)
    },

    /**
     * Returns a Boolean indicating if there is any coming soon content
     * to display.
     */
    comingSoonContentAvailable() {
      return Boolean((
        this.comingSoonContent?.text !== null &&
        this.comingSoonContent?.text !== 'null' &&
        this.comingSoonContent?.text !== '') ||
        this.comingSoonContent?.bullets?.length)
    },

    /**
     * Returns a list of links to be consumed by the clickable-list component
     * which controls the pdp sub-menu.
     * @returns {Array}
     */
    buildSubMenu() {
      const menu = []

      if (this.buildSubMenuDisplay.displayGarderob) {
        menu.push(
          {
            label: this.$string('product.sub_menu.garderob'),
            link: 'garderob',
          },
        )
      }

      if (this.buildSubMenuDisplay.displayPreOrder) {
        menu.push(
          {
            label: this.$string('product.sub_menu.preorder'),
            link: 'preorder',
          },
        )
      }

      if (this.buildSubMenuDisplay.displayComingSoon) {
        menu.push(
          {
            label: this.$string('product.sub_menu.coming_soon'),
            link: 'coming-soon',
          },
        )
      }

      if (this.buildSubMenuDisplay.displayDetails) {
        menu.push(
          {
            label: `${this.subMenuContent.details.label}`,
            link: 'details',
          },
        )
      }

      if (this.isSizeGuideAvailable || this.isOnlyBodyMeasurementAvailable) {
        if (this.isOnlyBodyMeasurementAvailable) {
          menu.push(
            {
              label: this.$string('product.sub_menu.measurements'),
              link: 'size-guide',
            },
          )
        } else {
          menu.push(
            {
              label: this.product.variants.length === 1 ? this.$string('product.sub_menu.measurements') : this.$string('product.sub_menu.size_and_fit'),
              link: 'size-guide',
            },
          )
        }
      }

      if (this.buildSubMenuDisplay.displayHelp) {
        menu.push(
          {
            label: this.$string('product.sub_menu.help'),
            link: 'help',
          },
        )
      }

      // Gift wrapping
      if (this.buildSubMenuDisplay.displayGiftWrapping) {
        menu.push(
          {
            label: this.$string('product.sub_menu.gift_wrapping'),
            link: 'gift-wrapping',
          },
        )
      }

      if (this.buildSubMenuDisplay.displaySustainability) {
        menu.push(
          {
            label: this.$string('product.sub_menu.sustainability'),
            link: 'sustainability',
          },
        )
      }

      if (this.buildSubMenuDisplay.displayDelivery) {
        menu.push(
          {
            label: this.$string('product.sub_menu.delivery'),
            link: 'delivery',
          },
        )
      }

      return menu
    },

    /**
     * Dynamically builds subMenu content when the product changes.
     * @returns {Array}
     */
    buildSubMenuContent() {
      const subMenu = JSON.parse(JSON.stringify(this.subMenuContent))
      subMenu.preorder = this.buildPreOrderContent

      subMenu.comingSoon = this.comingSoonContent

      /**
       * Add geo messaging in if it is available.
       */
      if (this.getRelevantMessaging?.menu_content && subMenu.delivery) {
        subMenu.delivery.geo = this.getRelevantMessaging.menu_content
      }

      if (!this.product?.subMenu) {
        return subMenu
      }

      for (const [key, value] of Object.entries(this.product.subMenu)) {
        subMenu[key] = value
      }

      subMenu.delivery = {
        geo: this.getRelevantMessaging?.menu_content,
        ...this.subMenuContent.delivery,
      }

      return subMenu
    },

    /**
     * Returns a dynamic list of sub menu links based on if that link has been
     * activated in section settings AND there is any content to display.
     * @returns {Object}
     */
    buildSubMenuDisplay() {
      return {
        displayGarderob: this.subMenuDisplay.displayGarderob &&
          this.menuItemHasContent(Object.values(this.buildSubMenuContent?.garderob)),
        displayDelivery: this.subMenuDisplay.displayDelivery &&
          this.menuItemHasContent(Object.values(this.buildSubMenuContent?.delivery)),
        displayDetails: this.subMenuDisplay.displayDetails &&
          this.menuItemHasContent(Object.values(this.buildSubMenuContent?.details)),
        displayMeasurements: this.subMenuDisplay.displayMeasurements &&
          this.menuItemHasContent(Object.values(this.buildSubMenuContent?.sizeGuide)),
        displayHelp: this.subMenuDisplay?.displayHelp,
        displayGiftWrapping: this.subMenuDisplay?.displayGiftWrapping,
        displaySustainability: this.subMenuDisplay.displaySustainability &&
          this.menuItemHasContent(Object.values(this.buildSubMenuContent?.sustainability)),
        displayPreOrder: this.preOrderContentAvailable && this.isPreorderProduct && this.isMobile,
        displayComingSoon: this.comingSoonContentAvailable &&
          this.isComingSoonProduct && this.isMobile,
      }
    },

    /**
     * Returns whether a product is a pre-order product or not.
     * @returns {Boolean}
     */
    isPreorderProduct() {
      return Boolean(this.product?.tags?.find(
        tag => tag.toLowerCase() === 'preorder',
      ))
    },

    /**
     * Returns a stripped product description from all the html characters
     * @returns {Boolean}
     */
    strippedProductDescription() {
      if (!this.product) {
        return []
      }

      return this.product.description.replace(/<[^>]*>?/gm, '')
    },

    /**
     * Returns whether a product is a final_sale product or not.
     * @returns {Boolean}
     */
    isFinalSaleProduct() {
      return Boolean(this.product?.tags?.find(
        tag => tag.toLowerCase() === 'final_sale',
      ))
    },

    /**
     * Returns whether a product is coming soon or not.
     * - If product is preorder, coming soon is always false
     * @returns {Boolean}
     */
    isComingSoonProduct() {
      if (this.isPreorderProduct) {
        return false
      }

      return Boolean(this.product?.tags?.find(
        tag => tag.toLowerCase().includes('coming-soon'),
      ))
    },

    /**
     * Returns whether a product is in private-sale or not.
     * - If product is preorder, private-sale is always false
     * @returns {Boolean}
     */
    isPrivateSaleProduct() {
      if (this.isPreorderProduct) {
        return false
      }

      return Boolean(this.product?.tags?.find(
        tag => tag.toLowerCase() === 'private-sale',
      ))
    },

    /**
     * Returns array of text in coming soon tags.
     * @returns {Array}
     */
    comingSoonText() {
      if (!this.isComingSoonProduct) {
        return []
      }

      const comingSoonTags = this.product?.tags?.filter(
        tag => tag.toLowerCase().includes('coming-soon:'),
      )

      const comingSoonText = comingSoonTags.forEach(
        tag => tag.replace('coming-soon:', ''),
      )

      return comingSoonText
    },

    /**
     * Returns a Boolean whether to display the colour swatches or not.
     * @returns {Boolean}
     */
    showProductSwatches() {
      const colourTag = this.product?.tags?.find(
        tag => tag.includes('color:'),
      )

      return Boolean(colourTag)
    },

    /**
     * Computes ometria list id based on product tags or global
     * settings.
     * Used for Coming Soon and Private Sale products
     * @returns {String}
     */
    productListId() {
      const ometriaTag = this.product?.tags?.find(
        tag => tag.toLowerCase().includes('ometria-list-id:'),
      )

      if (!ometriaTag) {
        const comingSoon = cnvs.ometria?.comingSoonListId
        const privateSale = cnvs.ometria?.privateSaleListId

        return this.isComingSoonProduct ? comingSoon : privateSale
      }

      return ometriaTag.replace('ometria-list-id:', '')
    },

    /**
     * Adds the additional image to the image array if product.images is odd.
     * @returns {Boolean, Array}
     */
    productImages() {
      if (!this.product) {
        return []
      }

      return this.product.video?.length
        ? [...this.product.images, ...this.product.video]
        : this.product.images
    },

    /**
     * Returns the component name of the presently selected sub-menu component.
     * @returns {String}
     */
    subMenuComponent() {
      if (!this.subMenuItem) {
        return false
      }

      return `sub-menu-${this.subMenuItem.link}`
    },

  },

  watch: {

    /**
     * Watch for the secondary menu being activated.
     */
    getActiveOverlay(newValue, oldValue) {
      if (!newValue && oldValue === 'secondaryMenu') {
        this.subMenuItem = null

        setTimeout(() => {
          this.handleMenuMouseLeave()
        }, this.$timing('quick'))
      }
    },

    /**
     * Watch handle for update to load new product.
     */
    handle(newValue, oldValue) {
      this.queryProduct(this.handle)
      this.updateProductUrl(newValue, oldValue)
    },

    product: {
      deep: true,
      immediate: true,
      handler() {
        this.updateProduct(this.product)
      },
    },

    /**
     * Watch window scroll position to update scroll direction
     */
    scrollY() {
      this.scrollDirection = this.scrollY > this.scrollPosition ? 'up' : 'down'
      this.scrollPosition = this.scrollY
    },
  },

  async created() {
    try {
      this.variant = JSON.parse(this.variantJson)
      this.$store.dispatch('product/setVariant', this.variant)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing JSON:', `${this.variant}`, error)
    }

    if (!this.useStoreAvailabilityFromLocations) {
      return
    }

    const coordinatesJson = window.sessionStorage.getItem('coordinates')
    if (coordinatesJson?.length) {
      this.coordinates = JSON.parse(coordinatesJson)
    } else {
      if (!navigator?.geolocation?.getCurrentPosition) {
        return
      }

      navigator.geolocation.getCurrentPosition((position) => {
        window.sessionStorage.setItem('coordinates', JSON.stringify({
          latitude: position?.coords?.latitude,
          longitude: position?.coords?.longitude,
        }))

        this.coordinates = position.coords
      })
    }

    const response = await fetch(`https://${this.appDomain}/api/${this.productId}/inventory?store_name=${this.storeName}`)
    const data = await response.json()

    if (!data?.ok) {
      return
    }

    this.variantAvailabilityData = data?.data?.product?.variants?.edges
  },

  mounted() {
    this.setProduct()
    this.calculateStickyElementsPosition()
    this.expandProductDescriptionCheck()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      updateProduct: 'product/setProduct',
    }),

    getDefaultAccordionItem() {
      return this.buildSubMenuDisplay.displayGiftWrapping === true ? 'gift-wrapping' : ''
    },

    /**
     * Check if the product description should be expanded
     */
    expandProductDescriptionCheck() {
      this.$nextTick().then(() => {
        if (this.customTag.toLowerCase().includes('garderob')) {
          this.handleExpandProductDescription(true)
        }
      })
    },

    /**
     * Sets the size guide status.
     * @param {Boolean} value - Emitted value from the product form component.
     */
    handleSizeGuideStatusChange(value) {
      this.sizeGuideDisplay = value
    },

    /**
     * Closes the zoomed state on the gallery image and displays the product
     * form.
     */
    closeZoom() {
      this.$refs.mobileGallery.handleCarouselImageClick()
    },

    /**
     * Handles the main product updating.
     * @param {Object} selectedProduct - The product that has been selected.
     */
    handleProductUpdate(selectedProduct) {
      this.handle = selectedProduct.handle
      cnvs.page.handle = selectedProduct.handle
    },

    /**
     * Utility function to return a true or false value if an object contains
     * a value with any content.
     * @param {Array} itemArray - Array of object values.
     * @returns {Boolean}
     */
    menuItemHasContent(itemArray) {
      if (!itemArray) {
        return false
      }

      return itemArray.some(item => {
        if (Array.isArray(item) && item.length) {
          return true
        }

        if (typeof item === 'string' && item && item !== 'null') {
          return true
        }

        return false
      })
    },

    /**
     * Handles the colour swatches updating.
     * @param {Object} product - Shopify product object.
     */
    handleSwatchUpdate() {
      this.$emit('update-product', product)
      this.cartError = null
    },

    /**
     * Handles updating the new product handle into the window URL.
     * @param {String} newValue - The updated product handle.
     * @param {String} oldValue - The previous product handle before the product
     * was updated.
     */
    updateProductUrl(newValue, oldValue) {
      const url = new URL(window.location.href)
      const pathname = url.pathname
      const newPathname = pathname.replace(oldValue, newValue)
      const newUrl = `${window.location.origin}${newPathname}`

      history.replaceState({}, '', newUrl)
    },

    /**
     * Handles when a sub menu link is clicked.
     * @param {Object} item - Sub menu item.
     */
    handleSubMenuClick(item) {
      if (item.link === this.subMenuItem?.link) {
        this.closeOverlay('secondaryMenu')
        this.subMenuItem = null
        return
      }

      this.subMenuItem = item
      this.calculateSecondaryMenuContentHeight()

      if (this.getActiveOverlay === 'secondaryMenu') {
        return
      }
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'secondaryMenu',
      })
    },

    /**
     * Handles expand description click on read more button
     * @param {Boolean} condition
     */
    handleExpandProductDescription(condition) {
      this.productDescriptionExpanded = condition
    },

    /**
     * Calculates the height for the sub-menu content and sets it to a css
     * variable.
     */
    calculateSecondaryMenuContentHeight() {
      const header = document.querySelector('.site-header').offsetHeight
      const secondaryMenu = this.$refs.secondaryMenu.$el
      const rect = secondaryMenu.getBoundingClientRect()
      const modalHeight = rect.top - header

      document.documentElement.style.setProperty(
        '--secondary-menu-content-height',
        `${modalHeight}px`,
      )
    },

    /**
     * Calculates the height of the sticky elements to stack them correctly
     */
    calculateStickyElementsPosition() {
      const formSection = this.$refs.formSection
      const descriptionSection = this.$refs.descriptionSection
      const secondaryMenuSection = this.$refs.secondaryMenuSection
      const completeTheLookSection = this.$refs.completeTheLookSection

      secondaryMenuSection?.style.setProperty(
        '--top',
        `${formSection.clientHeight}px`,
      )

      completeTheLookSection?.style.setProperty(
        '--top',
        `calc(100vh - ${completeTheLookSection.clientHeight}px - var(--spacing-2)`,
      )

      formSection?.style.setProperty(
        '--margin-bottom',
        `calc(${secondaryMenuSection.clientHeight + completeTheLookSection.clientHeight}px + var(--spacing-2))`,
      )

      descriptionSection?.style.setProperty(
        '--margin-top',
        `calc(-${secondaryMenuSection.clientHeight + completeTheLookSection.clientHeight}px - var(--spacing-2))`,
      )

      secondaryMenuSection?.style.setProperty(
        '--margin-bottom',
        `calc(${completeTheLookSection.clientHeight}px + var(--spacing-2))`,
      )
    },

    /**
     * Handles the emitted event when the gallery opens and closes. Updates the
     * gallery state in this component accordingly.
     * @param {String} value - The current state of the gallery component.
     */
    updateGalleryState(value) {
      this.galleryExpanded = value
    },

    /**
     * Handles the emitted event when the carousel index is updated.
     * @param {String} value - The current index of the carousel component.
     */
    updateGalleryIndex(value) {
      this.galleryIndex = value

      const contentContainer = this.$refs.contentContainer

      if (value > 1) {
        contentContainer.classList.add(this.$class('active'))
      } else {
        contentContainer.classList.remove(this.$class('active'))
      }
    },

    /**
     * Clears the clickable list component data when the mouse leaves the
     * component.
     */
    handleMenuMouseLeave() {
      const menu = this.$refs.secondaryMenu
      if (!menu || this.subMenuItem) {
        return
      }

      menu.resetComponentData()
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: product,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatPDPProduct(response),
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = new URL(window.location.href)

      if (variant?.id) {
        url.searchParams.set('variant', variant.id)
      } else {
        url.searchParams.delete('variant')
      }

      history.replaceState({
        html: '',
      }, '', url.href)
    },

    trackClick(breadcrumb) {
      const dataLayer = window.dataLayer || []

      dataLayer.push({
        event: 'breadcrumb_click',
        target_text: breadcrumb.text,
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
@import './sub-menu/sub-menu.scss';
</style>
